import { itemsInListOfType, VERIFIED_MAGIC_ID } from '@alltrails/shared/utils/lists/listHelpers';
import { PageStrings } from '@alltrails/shared/utils/constants/pageStringHelpers';
import { ServerCommunicationUtil } from '../../../../utils/server_communication_util';
import { StringConstantsMixin } from './shared/string_constants_mixin';
import { SearchFiltersUtil } from '../../../../utils/search_filters_util';

// Used by SearchApp (SearchMap/SearchResultList) and by FullScreenSearchResultList
const ResultCardFunctionsMixin = {
  allowAddRemove() {
    // Only allow on completed page, when admin or current user
    return (
      [
        PageStrings.EXPLORE_COMPLETED_PAGE,
        PageStrings.EXPLORE_FAVORITE_PAGE,
        PageStrings.EXPLORE_CUSTOM_PAGE,
        PageStrings.EXPLORE_USERS_TRACKS_PAGE,
        PageStrings.EXPLORE_USERS_MAPS_PAGE
      ].includes(this.state.page) &&
      this.props.context.currentUser &&
      this.props.userInfo &&
      (this.props.context.currentUser.admin || this.props.context.currentUser.id == this.props.userInfo.id)
    );
  },
  allowClickingCompletedBadge() {
    // Only allow on completed page, when admin
    return (
      this.state.page === PageStrings.EXPLORE_COMPLETED_PAGE &&
      this.props.context &&
      this.props.context.currentUser &&
      this.props.context.currentUser.admin
    );
  },
  forcePhotoId(type, objectId) {
    if (['map', 'track'].indexOf(type) === -1) {
      return null;
    }
    // Currently only works for map/track photos
    return this.state.forceTrackPhotoIds ? this.state.forceTrackPhotoIds[objectId] : this.props.initialPhotoId;
  },
  getAddRemoveLabels() {
    switch (this.state.page) {
      case PageStrings.EXPLORE_COMPLETED_PAGE:
        return {
          add: StringConstantsMixin.strings.SEARCH.ADD_TO_COMPLETED,
          remove: StringConstantsMixin.strings.SEARCH.REMOVE_FROM_COMPLETED
        };
      case PageStrings.EXPLORE_FAVORITE_PAGE:
        return {
          add: StringConstantsMixin.strings.SEARCH.ADD_TO_FAVORITES,
          remove: StringConstantsMixin.strings.SEARCH.REMOVE_FROM_FAVORITES
        };
      case PageStrings.EXPLORE_CUSTOM_PAGE:
        return {
          add: StringConstantsMixin.strings.SEARCH.ADD_TO_LIST,
          remove: StringConstantsMixin.strings.SEARCH.REMOVE_FROM_LIST
        };
      case PageStrings.EXPLORE_USERS_TRACKS_PAGE:
      case PageStrings.EXPLORE_USERS_MAPS_PAGE:
        return {
          add: null,
          remove: StringConstantsMixin.strings.DELETE
        };
      default:
        return {};
    }
  },
  handleAddRemoveClick(type, objectId) {
    switch (this.state.page) {
      case PageStrings.EXPLORE_COMPLETED_PAGE:
      case PageStrings.EXPLORE_FAVORITE_PAGE:
      case PageStrings.EXPLORE_CUSTOM_PAGE:
        return this.props.listMethods.toggleListItem(this.props.listId, type, objectId);
      case PageStrings.EXPLORE_USERS_TRACKS_PAGE:
      case PageStrings.EXPLORE_USERS_MAPS_PAGE:
        return this.handleDeleteMapClick(type, objectId);
      default:
        return null;
    }
  },
  handleDeleteMapClick(type, objectId) {
    if (!confirm(StringConstantsMixin.strings.DELETE_ITEM_QUESTION)) {
      return;
    }
    const url = `/api/alltrails/maps/${objectId}`;
    ServerCommunicationUtil.deleteApiEndpoint(
      url,
      {},
      () => {
        const results = this.state.results.filter(result => result.ID != objectId);
        this.setState({ results });
      },
      () => {},
      () => {}
    );
  },
  handleCompletedBadgeClick(type, objectId) {
    if (this.props.context.currentUser.admin) {
      // Only allow admins to manually verify ANY user's trails
      let userId;
      if (
        this.state.page === PageStrings.EXPLORE_COMPLETED_PAGE &&
        this.props.context.currentUser &&
        this.props.context.currentUser.admin &&
        this.props.userInfo &&
        this.props.context.currentUser.id != this.props.userInfo.id
      ) {
        userId = this.props.userInfo.id;
      } else {
        userId = null;
      }
      this.props.listMethods.toggleListItem(VERIFIED_MAGIC_ID, type, objectId, userId);
    }
  },
  isItemInPageList(type, objectId) {
    switch (this.state.page) {
      case PageStrings.EXPLORE_FAVORITE_PAGE:
        return this.props.listMethods.isFavorite(type, objectId);
      case PageStrings.EXPLORE_USERS_TRACKS_PAGE:
      case PageStrings.EXPLORE_USERS_MAPS_PAGE:
        return true;
      default:
        return this.props.listMethods.isItemInList(type, objectId, this.props.listId);
    }
  },
  // Updates filters/results in state based on list items added/removed ported from the old ListHashMixin
  updateResultsFromListHash({ action, listItem }, listItems) {
    const { listId } = this.props;
    let results = [...this.state.results];
    if (results) {
      if (parseInt(listItem.listId) === parseInt(listId)) {
        // Add/Remove from results
        results = results.filter(result => result.objectID !== `${listItem.type}-${listItem.objectId}`);
        if (action === 'add' && listItem.object) {
          // Add object onto the front of results so it appears at the top of the search result list on explore pages
          // Note: an item (currently) can't be added to a list from the list SEO page
          results.unshift(listItem.object);
        }
      }
    }

    const { filters } = this.state;
    if (filters) {
      const trailIds = Object.keys(itemsInListOfType(listId, 'trail', listItems));
      SearchFiltersUtil.setOrToggleFilter(filters, 'trailIds', trailIds);
    }

    if (results || filters) {
      const afterSetState = this.performSearch ? this.performSearch() : () => {};
      this.setState(
        {
          filters,
          results
        },
        afterSetState
      );
    }
  },
  // Catch-All for functions in this mixin
  // Keep this function at the bottom of this mixin
  getResultCardFunctions() {
    const self = this; // prevents weird scoping of `this` when inside object in ES6
    return {
      // Displaying photos
      forcePhotoId: self.forcePhotoId,
      // Displaying buttons
      allowClickingCompletedBadge: self.allowClickingCompletedBadge,
      allowAddRemove: self.allowAddRemove,
      // Filling button states
      getAddRemoveLabels: self.getAddRemoveLabels,
      isItemInPageList: self.isItemInPageList,
      // Handle clicking on entire result card (to best passed as handleCardClick)
      // These should be defined outside of the ResultCardFunctionsMixin
      handleAddRemoveClick: self.handleAddRemoveClick,
      handleMapClick: self.handleMapClick,
      handleTrackClick: self.handleTrackClick,
      handleTrailClick: self.handleTrailClick,
      formatTrailUrl: self.formatTrailUrl,
      // Handle clicking on buttons on result card
      handleCompletedBadgeClick: self.handleCompletedBadgeClick,
      handleFavoriteClick: self.handleFavoriteClick,
      handleLocationClick: self.handleTrailLocationClick
    };
  }
};

export { ResultCardFunctionsMixin };
